import { ArrowUp, Plus } from "@phosphor-icons/react";
import { forwardRef, type FormEvent, type ReactNode, type TextareaHTMLAttributes } from "react";
import { cn } from "@/lib/cn";
import { IconButton } from "../../actions/IconButton";

export type ChatComposerProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onSubmit"> & {
  value: string;
  onValueChange: (value: string) => void;
  onSubmit?: () => void;
  loading?: boolean;
  disabled?: boolean;
  /** `"sm"` is a compact composer for inline/sidebar placements; `"md"` is the default. */
  size?: "sm" | "md";
  attachmentSlot?: ReactNode;
  actionSlot?: ReactNode;
};

export const ChatComposer = forwardRef<HTMLTextAreaElement, ChatComposerProps>(
  ({ className, value, onValueChange, onSubmit, loading, disabled, size = "md", attachmentSlot, actionSlot, placeholder = "Ask me anything", ...props }, ref) => {
    const compact = size === "sm";
    const iconSize = compact ? 14 : 18;
    const iconButtonSize = compact ? "xs" : "sm";
    const compactIconButtonClassName = compact ? "bfrs-size-6 bfrs-rounded-lg" : undefined;

    const handleSubmit = (event: FormEvent) => {
      event.preventDefault();
      if (!disabled && !loading && value.trim()) onSubmit?.();
    };

    return (
      <form
        onSubmit={handleSubmit}
        className={cn(
          "bfrs-flex bfrs-items-end bfrs-rounded-lg bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-shadow-low focus-within:bfrs-border-border-focus focus-within:bfrs-ring-2 focus-within:bfrs-ring-border-focus/20",
          compact ? "bfrs-h-[42px] bfrs-items-center bfrs-gap-2 bfrs-p-[6px] bfrs-shadow-[0px_0px_12px_rgba(0,0,0,0.06)]" : "bfrs-min-h-14 bfrs-gap-2 bfrs-p-2",
          className
        )}
      >
        {attachmentSlot ?? (
          <IconButton aria-label="Attach file" type="button" icon={<Plus size={iconSize} />} variant={compact ? "secondary" : "ghost"} size={iconButtonSize} className={compactIconButtonClassName} disabled={disabled} />
        )}
        <textarea
          ref={ref}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          rows={1}
          className={cn(
            "bfrs-flex-1 bfrs-resize-none bfrs-bg-transparent bfrs-px-1 bfrs-leading-5 bfrs-text-text-primary bfrs-outline-none placeholder:bfrs-text-text-placeholder disabled:bfrs-cursor-not-allowed",
            compact ? "bfrs-max-h-28 bfrs-min-h-6 bfrs-py-px bfrs-text-[12px] bfrs-leading-[22.4px]" : "bfrs-max-h-36 bfrs-min-h-9 bfrs-py-2 bfrs-text-base"
          )}
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              if (value.trim()) onSubmit?.();
            }
          }}
          {...props}
        />
        {actionSlot}
        <IconButton
          aria-label="Send message"
          type="submit"
          icon={<ArrowUp size={iconSize} weight="bold" />}
          variant="primary"
          size={iconButtonSize}
          className={cn(compactIconButtonClassName, compact && "disabled:bfrs-bg-surface-muted disabled:bfrs-opacity-100")}
          loading={loading}
          disabled={disabled || !value.trim()}
        />
      </form>
    );
  }
);

ChatComposer.displayName = "ChatComposer";
